import { createFileRoute } from "@tanstack/react-router"
import {
  dashboardDateQuery,
  dashboardSelectionLabel,
  parseMarketplaceId,
  type DashboardDateSelection,
} from "@nova-ai/shared"
import { useEffect, useState, type ReactNode } from "react"

import { AppShell } from "../components/layout/app-shell"
import { QueryState } from "../components/layout/query-state"
import { MarketplaceMark } from "../components/marketplaces/marketplace-mark"
import { MetricTrend } from "../components/dashboard/metric-trend"
import { RevenueTrend } from "../components/dashboard/revenue-trend"
import { DashboardDatePicker } from "../components/dashboard/period-picker"
import { SalesChart } from "../components/dashboard/sales-chart"
import { Money } from "../components/ui/money"
import { useDashboard } from "../services/use-dashboard"
import { useOfferHealth } from "../services/use-offer-health"
import type { OfferHealthDetail, OfferHealthSnapshot } from "../services/use-offer-health"
import { useRates } from "../services/use-rates"
import { dashboardDateSearchSchema, selectionFromDashboardSearch } from "../lib/dashboard-period"
import { humanizeEnumLabel } from "../lib/enum-label"
import { formatRelativeTime } from "../lib/format-date"
import { cn } from "../lib/utils"
import { humanizeMarketplaceId } from "../lib/marketplace"
import { OfferHealthChart } from "../components/dashboard/offer-health-chart"
import { OrganicProductsTable } from "../components/dashboard/organic-products-table"
import {
  DEFAULT_ORGANIC_PRODUCT_ORDERING,
  useOrganicProducts,
  type OrganicProductOrdering,
} from "../services/use-organic-products"

function MarketplaceDashboard() {
  const { marketplace: marketplaceParam } = Route.useParams()
  const marketplace = parseMarketplaceId(marketplaceParam)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const selection = selectionFromDashboardSearch(search)
  const dashboard = useDashboard(marketplace, selection)
  const rates = useRates()
  const offerHealth = useOfferHealth(marketplace)
  // Local, not in the URL: the ranking is a way of reading the same ten rows,
  // not a different view worth sharing a link to.
  const [organicOrdering, setOrganicOrdering] = useState<OrganicProductOrdering>(
    DEFAULT_ORGANIC_PRODUCT_ORDERING
  )
  const organicProducts = useOrganicProducts(marketplace, selection, organicOrdering)

  const handleDateChange = (next: DashboardDateSelection) => {
    void navigate({ search: dashboardDateQuery(next) })
  }

  const currency = dashboard.data?.currency ?? "EUR"
  const selectionLabel = dashboard.data?.selectionLabel ?? dashboardSelectionLabel(selection)
  const trendTitle = dashboardComparisonLabel(dashboard.data?.comparisonLabel)

  return (
    <AppShell
      title={humanizeMarketplaceId(marketplace)}
      icon={<MarketplaceMark marketplace={marketplace} />}
      {...(dashboard.data?.storeUrl ? { titleHref: dashboard.data.storeUrl } : {})}
      actions={<DashboardDatePicker value={selection} onChange={handleDateChange} />}
      fullWidth
    >
      <QueryState isLoading={dashboard.isLoading} error={dashboard.error} isEmpty={!dashboard.data}>
        {dashboard.data && (
          <div className="space-y-6">
            <section className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
              {/* The headline figure leads on the LEFT and reads like the main
                  dashboard's portfolio statement: same 36px bold number, same
                  filled trend pill beside it, same caption beneath. The prose
                  sits bottom-right, on the caption's baseline, so the number
                  owns the top-left corner alone. */}
              <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-end sm:justify-between sm:p-6">
                <div>
                  <div className="text-[15px] font-semibold tracking-tight text-foreground">
                    Revenue trajectory
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <span className="text-[36px] font-semibold leading-none tracking-tight tabular-nums text-foreground">
                      <Money
                        amount={dashboard.data.kpis.gmv}
                        currency={currency}
                        rates={rates.data}
                        layout="stacked"
                      />
                    </span>

                    <RevenueTrend
                      current={dashboard.data.kpis.gmv}
                      previous={dashboard.data.previous.gmv}
                      comparisonLabel={trendTitle}
                      currency={currency}
                    />
                  </div>

                  <div className="mt-2 text-xs text-muted-foreground">
                    {`${dashboard.data.kpis.orderCount} order${
                      dashboard.data.kpis.orderCount === 1 ? "" : "s"
                    } · ${selectionLabel}`}
                  </div>
                </div>

                <p className="max-w-[20rem] text-xs text-muted-foreground sm:text-right">
                  Orders set the volume; the line follows normalized GMV.
                </p>
              </div>

              <div className="grid grid-cols-1 border-y border-border/70 bg-muted/[0.18] sm:grid-cols-3 sm:divide-x sm:divide-border/70">
                <MarketplaceMetricRow
                  label="Average basket"
                  value={
                    <Money
                      amount={dashboard.data.kpis.averageBasket}
                      currency={currency}
                      rates={rates.data}
                      layout="stacked"
                    />
                  }
                  trend={
                    <MetricTrend
                      current={dashboard.data.kpis.averageBasket}
                      previous={dashboard.data.previous.averageBasket}
                      title={trendTitle}
                    />
                  }
                />

                <MarketplaceMetricRow
                  label="Items per basket"
                  value={formatCount1(dashboard.data.kpis.itemsPerBasket)}
                  trend={
                    <MetricTrend
                      current={dashboard.data.kpis.itemsPerBasket}
                      previous={dashboard.data.previous.itemsPerBasket}
                      title={trendTitle}
                    />
                  }
                />

                <MarketplaceMetricRow
                  label="Canceled"
                  title={`Magento-canceled orders, ${selectionLabel.toLowerCase()}`}
                  value={dashboard.data.orders.canceled.toLocaleString()}
                  hint={`${formatCount1(dashboard.data.orders.canceledPct)}% of orders`}
                />
              </div>

              <div className="p-5 sm:p-6">
                <SalesChart
                  buckets={dashboard.data.chart.buckets}
                  granularity={dashboard.data.chart.granularity}
                  currency={dashboard.data.currency}
                  rates={rates.data}
                />
              </div>

              {/* Its own read, so a slow ad-fact aggregate never delays the
                  chart above it — the section simply appears when it lands. */}
              <OrganicProductsTable
                data={organicProducts.data}
                currency={currency}
                rates={rates.data}
                comparisonLabel={trendTitle}
                ordering={organicOrdering}
                onOrderingChange={setOrganicOrdering}
              />
            </section>

            <OfferHealthCard data={offerHealth.data} />
          </div>
        )}
      </QueryState>
    </AppShell>
  )
}

function dashboardComparisonLabel(label: string | undefined): string {
  return label ?? "vs previous range"
}

function MarketplaceMetricRow({
  label,
  value,
  trend,
  hint,
  title,
}: {
  label: string
  value: ReactNode
  trend?: ReactNode
  hint?: string
  title?: string
}) {
  return (
    <div className="px-5 py-2.5" title={title}>
      <div className="text-xs leading-tight text-muted-foreground">{label}</div>

      <div className="mt-0.5 flex items-baseline justify-between gap-3">
        <div className="text-lg font-semibold leading-tight tracking-tight tabular-nums text-foreground">
          {value}
        </div>

        {trend}
      </div>

      {hint ? (
        <div className="mt-0.5 text-xs leading-tight text-muted-foreground">{hint}</div>
      ) : null}
    </div>
  )
}

/** Locale-formatted number capped at one decimal ("2.5", "12", "33.3"). */
function formatCount1(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 })
}

function OfferActivitySummary({ latest }: { latest: OfferHealthSnapshot }) {
  const isActivityIncomplete = latest.unknown == null || latest.unknown > 0
  const isAllActive =
    latest.unknown === 0 &&
    latest.inactive === 0 &&
    latest.active === latest.total &&
    latest.total > 0
  const isEmpty =
    latest.unknown === 0 && latest.active === 0 && latest.inactive === 0 && latest.total === 0
  const unknownLabel =
    latest.unknown == null
      ? "unknown count unavailable"
      : `${latest.unknown.toLocaleString()} unknown`

  return (
    <>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tabular-nums text-foreground">
          {latest.active.toLocaleString()}
        </span>

        <span className="text-sm text-muted-foreground">
          / {latest.total.toLocaleString()} active
        </span>
      </div>

      <div
        className={cn(
          "mt-0.5 text-xs",
          latest.inactive > 0 || isActivityIncomplete ? "text-warning" : "text-muted-foreground"
        )}
      >
        {`${latest.inactive.toLocaleString()} inactive · ${unknownLabel}`}
      </div>

      {isAllActive ? <div className="mt-0.5 text-xs text-success">All offers active</div> : null}

      {isEmpty ? (
        <div className="mt-0.5 text-xs text-muted-foreground">No offers in snapshot</div>
      ) : null}
    </>
  )
}

// Offer coverage for the marketplace: active vs total with inactive and unknown
// counts, a small activity trend, and the inactivity-reason breakdown
// — the "why are offers being declined" view. Reads persisted snapshots; the
// daily `nova offers sync` appends them.
function OfferHealthCard({ data }: { data: OfferHealthDetail | undefined }) {
  const latest = data?.latest

  if (!latest) {
    return (
      <div className="rounded-xl border border-border bg-card p-5 shadow-card">
        <div className="text-sm font-medium text-foreground">Offer health</div>

        <p className="mt-3 text-sm text-muted-foreground">
          No offer snapshot yet. One appears after the daily nova offers sync cron runs.
        </p>
      </div>
    )
  }

  const reasons = Object.entries(latest.byInactivityReason)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
  const trend = data?.trend ?? []

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-baseline justify-between">
        <div className="text-sm font-medium text-foreground">Offer health</div>

        <span className="text-xs text-muted-foreground/60">
          <RelativeTime iso={latest.capturedAt} />
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Summary + inactivity-reason breakdown */}
        <div>
          <OfferActivitySummary latest={latest} />

          {reasons.length > 0 ? (
            <dl className="mt-3 space-y-1 border-t border-border/60 pt-3">
              {reasons.map(([reason, count]) => (
                <div key={reason} className="flex items-baseline justify-between gap-2 text-xs">
                  <dt className="truncate text-muted-foreground" title={reason}>
                    {humanizeEnumLabel(reason)}
                  </dt>

                  <dd className="shrink-0 tabular-nums text-foreground">
                    {count.toLocaleString()}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>

        {/* Active/inactive/unknown trend over time */}
        {trend.length > 1 ? (
          <div className="lg:col-span-2">
            <OfferHealthChart trend={trend} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

function RelativeTime({ iso }: { iso: string }) {
  const [label, setLabel] = useState(() => formatRelativeTime(iso))
  useEffect(() => {
    setLabel(formatRelativeTime(iso))
    const id = setInterval(() => setLabel(formatRelativeTime(iso)), 30_000)
    return () => clearInterval(id)
  }, [iso])
  return label
}

export const Route = createFileRoute("/dashboard/$marketplace")({
  validateSearch: dashboardDateSearchSchema,
  component: MarketplaceDashboard,
})
